import axios from 'axios';
import store from '@/store/index';

// 配置常量
const AUTH_CONFIG = {
  CLIENT_ID: process.env.VUE_APP_CLIENT_ID,
  CLIENT_SECRET: process.env.VUE_APP_CLIENT_SECRET,
  PROVISION_KEY: 'wiki-aggregation-layer-key',
  AUTH_SCOPES: 'email address'
};
  console.log('AUTH_CONFIG: ', AUTH_CONFIG);

const TOKEN_REFRESH_THRESHOLD = 3; // 最大重试次数
const DEFAULT_BFP = '822ca7b3261a0b2ec10c8cdabfb5a0ea';

// 状态管理
let isRefreshing = false;
const pendingRequests = [];
const retryCounter = new Map();

// 工具方法
const getStorageKey = () => `fx${store.state.base.userIp?.split('.').join('') || ''}`;
const getBaseParams = () => {
  const { isWiki, myCountry } = store.state.base;
  const bfp = localStorage.getItem('wiki-bfp') || DEFAULT_BFP;
  
  if (isWiki === '1') return `999,0,2,999,0,${bfp},0`;
  return myCountry === 'cn' 
    ? `999,0,1,225,1,${bfp},1`
    : `999,0,2,999,0,${bfp},0`;
};

// Token 管理
const tokenManager = {
  async getNewToken() {
    try {
      const authResponse = await axios.post(`${process.env.VUE_APP_JAVA}/oauth2/authorize`, {
        response_type: 'code',
        scope: AUTH_CONFIG.AUTH_SCOPES,
        client_id: AUTH_CONFIG.CLIENT_ID,
        provision_key: AUTH_CONFIG.PROVISION_KEY,
        authenticated_userid: 'authenticated_tester'
      });

      const code = new URL(authResponse.data.redirect_uri).searchParams.get('code');
      const tokenResponse = await axios.post(`${process.env.VUE_APP_JAVA}/oauth2/token`, {
        grant_type: 'authorization_code',
        client_id: AUTH_CONFIG.CLIENT_ID,
        client_secret: AUTH_CONFIG.CLIENT_SECRET,
        code
      });

      return this.storeToken(tokenResponse.data);
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  },

  storeToken({ access_token, expires_in }) {
    const storageKey = getStorageKey();
    const expiresAt = Date.now() + expires_in * 1000;
    
    localStorage.setItem(storageKey, access_token);
    localStorage.setItem('expiresIn', expiresAt.toString());
    
    return { token: access_token, expiresAt };
  },

  getStoredToken() {
    const storageKey = getStorageKey();
    const token = localStorage.getItem(storageKey);
    const expiresAt = parseInt(localStorage.getItem('expiresIn'), 10);
    
    return { token, expiresAt };
  }
};

// Axios 实例配置
const httpClient = axios.create({
  baseURL: process.env.VUE_APP_JAVA,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

// 请求拦截
httpClient.interceptors.request.use(async (config) => {
  const state = store.state.base;
  
  // 设置基础头信息
  config.headers = {
    ...config.headers,
    CountryCode: state.CountryCode || '156',
    'x-forword-for': state.userIp,
    LanguageCode: state.LanguageCode,
    BasicData: getBaseParams(),
    PreferredLanguageCode: state.preferredLanguageCode,
    ENVIRONMENT: state.nodeEnv,
    'X-User-Id': (state.user && state.user.userId) || '',
  };

  // Token 处理
  const { token, expiresAt } = tokenManager.getStoredToken();
  if (token && Date.now() < expiresAt) {
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  }

  if (!isRefreshing) {
    isRefreshing = true;
    try {
      const { token: newToken } = await tokenManager.getNewToken();
      config.headers.Authorization = `Bearer ${newToken}`;
      pendingRequests.forEach(cb => cb(newToken));
      pendingRequests.length = 0;
    } finally {
      isRefreshing = false;
    }
    return config;
  }

  return new Promise((resolve) => {
    pendingRequests.push((newToken) => {
      config.headers.Authorization = `Bearer ${newToken}`;
      resolve(config);
    });
  });
});

// 响应拦截
httpClient.interceptors.response.use(
  (response) => {
    console.log('response: ', response);
    // const { succeed, ErrorCode, data } = response.data;
    // console.log('succeed, ErrorCode, data : ', succeed, ErrorCode, data );
    // return ErrorCode === 200 && succeed ? data : Promise.reject(response.data);
    const { succeed = true, ErrorCode = 200, data = '' } = response?.data
    console.log(9999812,ErrorCode,succeed,data,response);
    
    if (ErrorCode != 200 || !succeed) return process?.client ? (data || response?.data) : null // 数据返回不正常，客户端请求的接口返回错误数据，服务端请求的接口进入错误页面
    else return (typeof data != 'number' ? (data || response.data) : response?.data) // 两者只要有一个字段存在数据，就返回数据
  },
  async (error) => {
    const { config, response } = error;
    if (!config || !response) return Promise.reject(error);

    // 处理 401 错误
    if (response.status === 401) {
      const urlKey = config.url;
      const retryCount = retryCounter.get(urlKey) || 0;

      if (retryCount >= TOKEN_REFRESH_THRESHOLD) {
        retryCounter.delete(urlKey);
        return Promise.reject(error);
      }

      retryCounter.set(urlKey, retryCount + 1);

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const { token } = await tokenManager.getNewToken();
          config.headers.Authorization = `Bearer ${token}`;
          retryCounter.delete(urlKey);
          return httpClient(config);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return new Promise((resolve) => {
        pendingRequests.push((newToken) => {
          config.headers.Authorization = `Bearer ${newToken}`;
          resolve(httpClient(config));
        });
      });
    }

    return Promise.reject(error);
  }
);

export default httpClient;
export { getBaseParams };